import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Container,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputAdornment,
  IconButton,
  Avatar,
  Checkbox,
  Link
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import { API_ENDPOINTS, apiRequest } from '../config/api';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiRequest(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        body: JSON.stringify({ username, password, role }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('user', JSON.stringify(data.user));

      switch (data.user.role) {
        case 'admin':
          navigate('/admin-dashboard');
          break;
        case 'teacher':
          navigate('/teacher-dashboard');
          break;
        case 'student':
          navigate('/student-dashboard');
          break;
        default:
          navigate('/');
          break;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#e3eafc',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          display: 'flex',
          width: { xs: '100%', sm: 800 },
          minHeight: 500,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: 8,
        }}
      >
        {/* Left Section */}
        <Box
          sx={{
            flex: 1,
            background: 'linear-gradient(135deg, #1976d2 60%, #42a5f5 100%)',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            p: 4,
          }}
        >
          {/* Bubbles */}
          <Box
            sx={{
              position: 'absolute',
              top: 30,
              left: 30,
              width: 120,
              height: 120,
              bgcolor: 'rgba(255,255,255,0.08)',
              borderRadius: '50%',
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 40,
              right: 40,
              width: 90,
              height: 90,
              bgcolor: 'rgba(255,255,255,0.10)',
              borderRadius: '50%',
              zIndex: 0,
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 500, letterSpacing: 2, mb: 2, textAlign: 'left' }}
            >
              WELCOME TO
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                letterSpacing: 2,
                mb: 1,
                color: '#fff',
                textShadow: '0 2px 12px rgba(0,0,0,0.10)',
                textAlign: 'left',
              }}
            >
              ViGiLANT
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 400, opacity: 0.95, textAlign: 'left', mb: 0 }}
            >
              Online Exam Management
            </Typography>
          </Box>
        </Box>

        {/* Right Section (Login Form) */}
        <Box
          sx={{
            flex: 1.2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, sm: 5 },
            background: '#fff',
          }}
        >
          <Avatar sx={{ bgcolor: '#1976d2', width: 60, height: 60, mb: 2 }}>
            <PersonIcon sx={{ fontSize: 38 }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Sign in
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
            Please enter your credentials to access your account.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }} icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', maxWidth: 340 }}>
            <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
                Select Role
              </FormLabel>
              <RadioGroup
                row
                name="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <FormControlLabel value="admin" control={<Radio />} label="Admin" />
                <FormControlLabel value="teacher" control={<Radio />} label="Teacher" />
                <FormControlLabel value="student" control={<Radio />} label="Student" />
              </RadioGroup>
            </FormControl>

            <TextField
              required
              fullWidth
              id="username"
              name="username"
              label="Username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              size="medium"
              sx={{ mb: 2 }}
            />

            <TextField
              required
              fullWidth
              name="password"
              id="password"
              label="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              size="medium"
              type={showPassword ? 'text' : 'password'}
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography variant="body2">Remember me</Typography>}
                sx={{ m: 0 }}
              />
              <Link href="#" variant="body2" sx={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}>
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                py: 1.3,
                fontWeight: 700,
                fontSize: '1.1rem',
                textTransform: 'uppercase',
                backgroundColor: '#1976d2',
                borderRadius: 2,
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
              disabled={loading}
            >
              {loading ? 'SIGNING IN...' : 'SIGN IN'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default Login;
